/**
 * Timezone-aware calendar helpers: read an instant as a calendar date in an
 * IANA zone, and turn a zone's local midnight back into a UTC instant. Shared
 * by the calendar-to-date periods (`lastUpdatedPeriodStart`) and the client
 * list's "more than … ago" cutoff (`olderThanCutoff`).
 */

/** Sunday-first weekday name → Monday-first offset (0 = Monday ... 6 = Sunday). */
const MONDAY_FIRST_DOW: Record<string, number> = {
  Mon: 0,
  Tue: 1,
  Wed: 2,
  Thu: 3,
  Fri: 4,
  Sat: 5,
  Sun: 6,
};

/**
 * One `Intl.DateTimeFormat` per (kind, zone): construction is the expensive
 * part (~25µs), formatting is cheap, and the set of zones in use is small.
 * A zone Intl rejects throws on construction and is never cached.
 */
const formatterCache = new Map<string, Intl.DateTimeFormat>();

function cachedFormatter(
  kind: string,
  timeZone: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${kind}|${timeZone}`;
  let formatter = formatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", { ...options, timeZone });
    formatterCache.set(key, formatter);
  }
  return formatter;
}

/** The calendar date (and Monday-first weekday) `date` reads as in `timeZone`. */
export function zonedDateParts(
  date: Date,
  timeZone: string,
): { year: number; month: number; day: number; mondayOffset: number } {
  const parts = cachedFormatter("date", timeZone, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return {
    year: Number(get("year")),
    month: Number(get("month")) - 1, // 0-indexed, to match `Date`
    day: Number(get("day")),
    mondayOffset: MONDAY_FIRST_DOW[get("weekday")] ?? 0,
  };
}

/**
 * The UTC instant of local midnight (00:00) on `year`-`month`-`day` (month
 * 0-indexed, may be out of range — `Date.UTC` normalises it) in `timeZone`.
 *
 * Converts by round-tripping a UTC guess through the zone's own wall-clock
 * reading of it: format the guess in `timeZone`, measure how far that
 * reading drifted from the guess, and undo the drift. Exact away from a DST
 * transition; off by at most the transition's own size exactly at one —
 * the same tolerance `schedule-utils.ts`'s Oslo-specific offset probing
 * accepts, generalised here to an arbitrary IANA zone.
 */
export function zonedMidnightToUtc(
  year: number,
  month: number,
  day: number,
  timeZone: string,
): Date {
  const utcGuess = Date.UTC(year, month, day, 0, 0, 0);
  const parts = cachedFormatter("datetime-seconds", timeZone, {
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(utcGuess));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const readAsUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour"),
    get("minute"),
    get("second"),
  );
  return new Date(utcGuess - (readAsUtc - utcGuess));
}

/** The default tenant time zone (QL-689) and the zone "today" is read in where no tenant zone is wired yet (QL-692). */
export const DEFAULT_TENANT_TIME_ZONE = "Europe/Oslo";

/** Today's calendar date in `timeZone` as "yyyy-MM-dd". */
export function todayInZone(
  timeZone: string = DEFAULT_TENANT_TIME_ZONE,
  now: Date = new Date(),
): string {
  const { year, month, day } = zonedDateParts(now, timeZone);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${String(year).padStart(4, "0")}-${pad(month + 1)}-${pad(day)}`;
}

/** Whether the runtime accepts `value` as an IANA zone name — Intl.DateTimeFormat throws on anything else. */
export function isValidTimeZone(value: unknown): value is string {
  if (typeof value !== "string" || value === "") return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

/** `value` when it is a valid IANA zone, otherwise DEFAULT_TENANT_TIME_ZONE. */
export function coerceTimeZone(value: string | null | undefined): string {
  return isValidTimeZone(value) ? value : DEFAULT_TENANT_TIME_ZONE;
}

/** The wall clock `date` reads as in `timeZone`: calendar parts (month 0-indexed like `Date`) plus 24-hour hour/minute. */
export function zonedDateTimeParts(
  date: Date,
  timeZone: string,
): { year: number; month: number; day: number; hour: number; minute: number } {
  const parts = cachedFormatter("datetime", timeZone, {
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(date);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return {
    year: get("year"),
    month: get("month") - 1,
    day: get("day"),
    hour: get("hour"),
    minute: get("minute"),
  };
}
